(() => {
  const closestFromEvent = (event, selector) => {
    const t = event && event.target;
    return t instanceof Element ? t.closest(selector) : null;
  };

  const ROOT_SELECTOR = '.kozler-cart[data-section-id]';

  const initKozlerCartDrawer = (root) => {
    if (!root || root.dataset.kozlerInitialized === 'true') return;
    const content = root.querySelector('[data-cart-content]');
    if (!content) return;
    root.dataset.kozlerInitialized = 'true';
    root.style.display = '';
    const sectionId = root.dataset.sectionId || '';
    const badge = root.querySelector('[data-cart-count]');
    const clearButton = root.querySelector('[data-clear-cart]');
    const shareButton = root.querySelector('[data-cart-share]');
    const shareLabel = shareButton?.querySelector('[data-share-label]');
    const shareDefaultLabel = shareLabel ? shareLabel.textContent : '';
    const shareCartPath = shareButton?.dataset.cartUrl || root.dataset.cartUrl || '/cart';
    const OPEN_ON_SUBMIT_ATTR = 'data-kozler-cart-open-on-submit';
    let shareLabelTimer;
    let noteTimer;
    let lastOpenOnSubmit = null;
    let lastOpenOnSubmitAt = 0;
    let lastKnownCartProductIds = new Set();
    let lastKnownCartVariantIds = new Set();

    const normalizeCount = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const emitCartCount = (count) => {
      const detail = { count: normalizeCount(count) };
      document.dispatchEvent(new CustomEvent('kozler:cart:count', { detail }));
    };

    const headerCartCountSrLabel = (btn, nextCount) => {
      if (!btn || nextCount <= 0) return '';
      const one = btn.dataset.cartCountLabelOne;
      const other = btn.dataset.cartCountLabelOther;
      if (one && other) {
        if (nextCount === 1) return one;
        return other.replace(/^\d+/, String(nextCount));
      }
      return nextCount === 1 ? '1 item' : `${nextCount} items`;
    };

    const syncHeaderCartBadges = (count) => {
      const nextCount = normalizeCount(count);
      const badges = document.querySelectorAll('.cart-qty');
      badges.forEach((badgeEl) => {
        const btn = badgeEl.closest('[data-kozler-cart-open], [data-cart-icon]');
        let valueEl = badgeEl.querySelector('[data-kozler-cart-count-value]');
        if (!valueEl) {
          valueEl = document.createElement('span');
          valueEl.setAttribute('data-kozler-cart-count-value', '');
          valueEl.setAttribute('aria-hidden', 'true');
          badgeEl.appendChild(valueEl);
        }
        valueEl.textContent = String(nextCount);
        badgeEl.classList.toggle('opacity-0', nextCount <= 0);
        const srEl = badgeEl.querySelector('[data-kozler-cart-count-sr]');
        if (srEl && btn) {
          const label = headerCartCountSrLabel(btn, nextCount);
          srEl.textContent = label || '';
        }
      });
    };

    const syncDrawerCountBadge = (count) => {
      if (!badge) return;
      const nextCount = normalizeCount(count);
      badge.setAttribute('data-count-value', String(nextCount));
      let valueEl = badge.querySelector('[data-cart-count-value]');
      if (!valueEl) {
        valueEl = document.createElement('span');
        valueEl.className = 'kozler-cart__count-value';
        valueEl.setAttribute('data-cart-count-value', '');
        badge.textContent = '';
        badge.appendChild(valueEl);
      }
      valueEl.textContent = String(nextCount);
    };

    const updateHeaderActions = (count) => {
      const nextCount = normalizeCount(count);
      root.setAttribute('data-cart-count', String(nextCount));
      const hasItems = nextCount > 0;
      if (clearButton) {
        clearButton.toggleAttribute('hidden', !hasItems);
      }
      if (shareButton) {
        shareButton.toggleAttribute('hidden', !hasItems);
      }
      syncDrawerCountBadge(nextCount);
      syncHeaderCartBadges(nextCount);
      emitCartCount(nextCount);
    };

    updateHeaderActions(root.dataset.cartCount || 0);

    const getSectionEndpoint = () => {
      const url = new URL(window.location.href);
      if (sectionId) url.searchParams.set('section_id', sectionId);
      url.searchParams.set('kozler_cache', Date.now().toString());
      return url.toString();
    };

    let busyCount = 0;

    const getCheckoutElements = () => ({
      button: root.querySelector('[data-checkout-button]'),
      dynamic: root.querySelector('.kozler-cart__dynamic-checkout'),
    });

    const updateCheckoutState = () => {
      const terms = getTermsElements();
      const termsChecked = terms ? !!terms.checkbox?.checked : true;
      const termsDisabled = terms ? (terms.required && !termsChecked) : false;
      const busy = busyCount > 0;
      const { button, dynamic } = getCheckoutElements();

      if (button) {
        button.toggleAttribute('disabled', busy);
        button.setAttribute('aria-disabled', busy ? 'true' : 'false');
        button.classList.toggle('is-disabled', busy);
      }
      if (dynamic) {
        const disableDynamic = busy || termsDisabled;
        dynamic.classList.toggle('is-disabled', disableDynamic);
        dynamic.toggleAttribute('aria-disabled', disableDynamic);
      }
    };

    const setBusy = (state) => {
      if (state) {
        busyCount += 1;
      } else {
        busyCount = Math.max(0, busyCount - 1);
      }
      root.classList.toggle('is-loading', busyCount > 0);
      updateCheckoutState();
    };

    const startControlLoading = (el) => {
      if (!el) return;
      el.classList.add('is-loading');
      el.setAttribute('aria-busy', 'true');
    };

    const stopControlLoading = (el) => {
      if (!el) return;
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
    };

    const resolveControlLoadingTarget = (el) => {
      if (!el || !el.matches) return el;
      if (el.matches('[data-upsell-toggle]')) {
        return el.closest('[data-upsell-toggle-wrap]') || el.closest('.kozler-oneclick__control') || el;
      }
      return el;
    };

    const formatMoney = (cents) => {
      const mf = root.dataset.moneyFormat || '';
      const currency = root.dataset.currency || 'USD';
      if (window.Shopify && window.Shopify.formatMoney) {
        try {
          return window.Shopify.formatMoney(cents, mf);
        } catch (e) {
          /* noop */
        }
      }
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    };

    const normalizeMoneyCents = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.round(parsed) : 0;
    };

    const getFreePriceLabel = () => {
      const raw = root.dataset.freePriceLabel;
      const label = typeof raw === 'string' ? raw.trim() : '';
      return label || 'FREE';
    };

    const formatMoneyOrFree = (cents) => {
      if (normalizeMoneyCents(cents) === 0) return getFreePriceLabel();
      return formatMoney(cents);
    };

    const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();
    const escapeHtmlAttr = (value) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const getUpsellPriceMarkup = (priceCents) => {
      const normalizedPrice = normalizeMoneyCents(priceCents);
      return `<span class="kozler-cart-item__price--current">${formatMoneyOrFree(normalizedPrice)}</span>`;
    };

    const getUpsellAddButtonLabel = (baseLabel, priceCents, showPrice) => {
      const label = String(baseLabel || 'Add');
      if (!showPrice) return label;
      const normalizedPrice = normalizeMoneyCents(priceCents);
      const priceText = normalizedPrice === 0 ? getFreePriceLabel() : stripHtml(formatMoney(normalizedPrice));
      return `${label} - ${priceText}`;
    };

    const getUpsellControlStyle = (list) => {
      const style = (list?.dataset?.upsellControlStyle || 'button').toLowerCase();
      if (style === 'checkbox' || style === 'switch') return style;
      return 'button';
    };

    const shouldHideUpsellWhenAdded = (list) => list?.dataset?.hideWhenAdded === 'true';
    const shouldShowUpsellAddedState = (list) => list?.dataset?.showAddedState === 'true';
    const shouldUpsellLabelSwitchToAddedState = (list) => list?.dataset?.upsellLabelSwitchesAddedState !== 'false';

    const getUpsellAddedLabel = (list, labelEl) => {
      const fromLabel = labelEl?.getAttribute('data-added-label');
      const fromList = list?.dataset?.addedLabel;
      return String(fromLabel || fromList || 'Added');
    };

    const syncUpsellPrice = (upsellItem, priceCents) => {
      const priceEl = upsellItem?.querySelector('[data-upsell-price]');
      if (!priceEl) return;
      priceEl.innerHTML = getUpsellPriceMarkup(priceCents);
    };

    const setShareLabel = (text, autoReset = false) => {
      if (!shareLabel) return;
      shareLabel.textContent = text;
      if (autoReset && shareDefaultLabel) {
        clearTimeout(shareLabelTimer);
        shareLabelTimer = setTimeout(() => {
          shareLabel.textContent = shareDefaultLabel;
        }, 1600);
      }
    };

    const buildCartShareUrl = (cartState) => {
      const items = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      if (!items.length) return null;
      const parts = items
        .map((item) => {
          if (!item || !item.variant_id || !item.quantity) return null;
          return `${item.variant_id}:${item.quantity}`;
        })
        .filter(Boolean);
      if (!parts.length) return null;
      const cartBase = shareCartPath && shareCartPath !== '' ? shareCartPath : '/cart';
      const cleanedCartBase = cartBase.replace(/\/$/, '');
      const basePath = `${cleanedCartBase}/${parts.join(',')}`;

      return `${window.location.origin}${basePath}/checkout`;
    };

    const copyCartLink = (url) => {
      if (!url) return Promise.reject(new Error('Missing cart link'));
      const fallbackCopy = () => new Promise((resolve, reject) => {
        try {
          const textarea = document.createElement('textarea');
          textarea.value = url;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
          resolve();
        } catch (error) {
          reject(error);
        }
      });

      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(url).catch(() => fallbackCopy());
      }
      return fallbackCopy();
    };

    const validateNoteRequirement = () => {
      const noteBlock = content.querySelector('[data-note-block]');
      if (!noteBlock) return true;
      const required = noteBlock.getAttribute('data-note-required') === 'true';
      if (!required) return true;
      const textarea = noteBlock.querySelector('[data-cart-note]');
      const error = noteBlock.querySelector('[data-note-error]');
      if (!textarea) return true;
      const value = (textarea.value || '').trim();
      if (value.length === 0) {
        if (error) error.hidden = false;
        noteBlock.classList.add('has-error');
        const body = noteBlock.querySelector('.kozler-cart__note-body');
        const toggle = noteBlock.querySelector('[data-note-toggle]');
        if (body && body.hasAttribute('hidden')) {
          body.removeAttribute('hidden');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
        textarea.focus();
        return false;
      }
      if (error) error.hidden = true;
      noteBlock.classList.remove('has-error');
      return true;
    };

    const getTermsElements = () => {
      const block = content.querySelector('[data-terms-block]');
      if (!block) return null;
      return {
        block,
        required: block.getAttribute('data-terms-required') === 'true',
        checkbox: block.querySelector('[data-terms-checkbox]'),
        error: block.querySelector('[data-terms-error]'),
        dynamicCheckout: content.querySelector('.kozler-cart__dynamic-checkout'),
      };
    };

    const updateTermsState = () => {
      const terms = getTermsElements();
      if (!terms) {
        updateCheckoutState();
        return;
      }
      const checked = !!terms.checkbox?.checked;
      const shouldDisable = terms.required && !checked;
      if (terms.checkbox) {
        terms.checkbox.setAttribute('aria-invalid', shouldDisable ? 'true' : 'false');
      }
      updateCheckoutState();
    };

    const validateTermsRequirement = () => {
      const terms = getTermsElements();
      if (!terms || !terms.required) return true;
      const checked = !!terms.checkbox?.checked;
      if (!checked) {
        if (terms.error) terms.error.hidden = false;
        terms.block.classList.add('has-error');
        updateTermsState();
        terms.checkbox?.focus();
        return false;
      }
      if (terms.error) terms.error.hidden = true;
      terms.block.classList.remove('has-error');
      updateTermsState();
      return true;
    };

    const shareCart = () => {
      if (!shareButton) return;
      startControlLoading(shareButton);
      shareButton.disabled = true;
      return fetch('/cart.js')
        .then((res) => res.json())
        .then((cartState) => {
          const shareUrl = buildCartShareUrl(cartState);
          if (!shareUrl) {
            setShareLabel('Empty', true);
            throw new Error('Cart is empty');
          }
          return copyCartLink(shareUrl).then(() => {
            setShareLabel('Copied!', true);
          });
        })
        .catch((error) => {
          console.error('Cart share failed', error);
          setShareLabel('Error', true);
        })
        .finally(() => {
          stopControlLoading(shareButton);
          shareButton.disabled = false;
        });
    };

    const clearCart = () => {
      if (!clearButton) return;
      startControlLoading(clearButton);
      clearButton.disabled = true;
      setBusy(true);
      return fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => refreshDrawer())
        .catch((error) => {
          console.error('Cart clear failed', error);
        })
        .finally(() => {
          stopControlLoading(clearButton);
          clearButton.disabled = false;
          setBusy(false);
        });
    };

    const AUTO_ADD_SUPPRESS_ATTR = 'kozler_auto_add_suppressed';
    const autoAddSuppressed = new Set();
    const smartRecoCache = new Map();

    const collectAutoAddRules = () => {
      const rules = [];
      content.querySelectorAll('[data-auto-add-rule]').forEach((el) => {
        const trigger = el.getAttribute('data-trigger-variant');
        const target = el.getAttribute('data-target-variant');
        const qty = parseInt(el.getAttribute('data-target-qty') || '1', 10) || 1;
        if (trigger && target) {
          const ruleId = el.getAttribute('data-rule-id') || `${trigger}:${target}`;
          rules.push({ id: ruleId, trigger: String(trigger), target: String(target), qty });
        }
      });
      return rules;
    };

    const parseSuppressedRules = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map(String).filter(Boolean);
      return String(value)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    };

    const loadSuppressedRules = (cartState) => {
      const attrValue = cartState && cartState.attributes ? cartState.attributes[AUTO_ADD_SUPPRESS_ATTR] : '';
      parseSuppressedRules(attrValue).forEach((id) => autoAddSuppressed.add(id));
    };

    const updateCartAttributes = (attributes) => {
      setBusy(true);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      })
        .catch((error) => {
          console.error('Cart attribute update failed', error);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const setSuppressedRules = (ruleIds) => {
      ruleIds.forEach((id) => autoAddSuppressed.add(String(id)));
      const nextValue = Array.from(autoAddSuppressed).join(',');
      return updateCartAttributes({ [AUTO_ADD_SUPPRESS_ATTR]: nextValue });
    };

    const suppressAutoAddForVariant = (variantId) => {
      const rules = collectAutoAddRules();
      const targets = rules.filter((rule) => rule.target === String(variantId)).map((rule) => rule.id);
      if (!targets.length) return;
      setSuppressedRules(targets);
    };

    const parseIdCsv = (value) =>
      String(value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

    /** Program-only SKUs (no bundled equipment) — never hide the equipment upsell or treat as "combo satisfied". */
    const STANDALONE_PROGRAM_VARIANT_IDS = new Set(['45142345023674']);

    const filterBundleVariantIdList = (ids) =>
      ids.filter((id) => !STANDALONE_PROGRAM_VARIANT_IDS.has(String(id)));

    /**
     * Reset & Reward Challenge already ships the equipment bundle inside the program,
     * so the grey equipment upsell must be suppressed whenever any of these variants
     * is in the cart. Kept as an additive check so existing `data-hide-when-bundle-variant-ids`
     * and `alternate_satisfied_variant_ids` logic is preserved unchanged.
     */
    const RESET_REWARD_BUNDLE_VARIANT_IDS = new Set(['45727913869498']);

    const isResetRewardBundleInCart = () => {
      for (const id of RESET_REWARD_BUNDLE_VARIANT_IDS) {
        if (lastKnownCartVariantIds.has(String(id))) return true;
      }
      return false;
    };

    const setLastKnownCartProductIds = (cartState) => {
      const cartItems = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      lastKnownCartProductIds = new Set(
        cartItems
          .map((item) => String(item && item.product_id ? item.product_id : ''))
          .filter(Boolean)
      );
      lastKnownCartVariantIds = new Set(
        cartItems
          .map((item) => String(item && item.variant_id ? item.variant_id : ''))
          .filter(Boolean)
      );
    };

    const getUpsellAddedStorageKey = (list) => {
      const blockId = list?.dataset?.upsellBlockId;
      if (!blockId || !sectionId) return null;
      return `kozler_upsell_added:${sectionId}:${blockId}`;
    };

    const getStoredUpsellProductIds = (list) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key) return [];
      try {
        return parseIdCsv(window.sessionStorage.getItem(key));
      } catch (error) {
        return [];
      }
    };

    const setStoredUpsellProductIds = (list, productIds) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key) return;
      try {
        const normalized = Array.from(new Set(parseIdCsv((productIds || []).join(','))));
        if (!normalized.length) {
          window.sessionStorage.removeItem(key);
          return;
        }
        window.sessionStorage.setItem(key, normalized.join(','));
      } catch (error) {
        /* noop */
      }
    };

    const rememberUpsellProductId = (list, productId) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key || !productId) return;
      try {
        const existingIds = getStoredUpsellProductIds(list);
        const normalizedId = String(productId);
        if (existingIds.includes(normalizedId)) return;
        existingIds.push(normalizedId);
        window.sessionStorage.setItem(key, existingIds.join(','));
      } catch (error) {
        /* noop */
      }
    };

    const forgetUpsellProductId = (list, productId) => {
      if (!list || !productId) return;
      const normalized = String(productId);
      const nextIds = getStoredUpsellProductIds(list).filter((id) => String(id) !== normalized);
      setStoredUpsellProductIds(list, nextIds);
    };

    const syncUpsellToggleLabel = (upsellItem, upsellList, selectedVariantInCart) => {
      if (!upsellItem || !upsellList) return;
      const toggleLabel = upsellItem.querySelector('[data-upsell-toggle-label]');
      const toggleWrap = upsellItem.querySelector('[data-upsell-toggle-wrap]');
      if (!toggleLabel) return;
      const showAddedState = shouldShowUpsellAddedState(upsellList);
      const swapLabelWhenAdded = shouldUpsellLabelSwitchToAddedState(upsellList);
      const addedLabel = getUpsellAddedLabel(upsellList, toggleLabel);
      const addLabel = toggleLabel.getAttribute('data-add-label') || toggleLabel.getAttribute('data-base-label') || upsellList.dataset.buttonLabel || 'Add';
      const useAddedLabel = showAddedState && selectedVariantInCart && swapLabelWhenAdded;
      toggleLabel.textContent = useAddedLabel ? addedLabel : addLabel;
      if (toggleWrap) {
        toggleWrap.classList.toggle('is-added', useAddedLabel);
      }
    };

    const syncUpsellItemState = (upsellItem, cartProductIds = lastKnownCartProductIds, cartVariantIds = lastKnownCartVariantIds) => {
      if (!upsellItem) return;
      const upsellList = upsellItem.closest('.kozler-cart-upsell__list');
      if (!upsellList) return;

      const productId = String(upsellItem.getAttribute('data-upsell-product-id') || '');
      const productInCart = productId !== '' && cartProductIds.has(productId);
      const alternateSatisfiedIds = filterBundleVariantIdList(
        parseIdCsv(upsellList.dataset.alternateSatisfiedVariantIds || ''),
      );
      const satisfiedByAlternateVariant =
        alternateSatisfiedIds.length > 0 && alternateSatisfiedIds.some((id) => cartVariantIds.has(id));
      const hideWhenAdded = shouldHideUpsellWhenAdded(upsellList);
      const controlStyle = getUpsellControlStyle(upsellList);

      if (hideWhenAdded && (productInCart || satisfiedByAlternateVariant)) {
        upsellItem.hidden = true;
        upsellItem.style.display = 'none';
      } else {
        upsellItem.hidden = false;
        upsellItem.style.display = '';
      }

      if (controlStyle === 'button') return;

      const toggle = upsellItem.querySelector('[data-upsell-toggle]');
      if (!toggle) return;
      const variantSelect = upsellItem.querySelector('[data-upsell-variant-select]');
      const variantId = String(
        (variantSelect && variantSelect.value)
          || toggle.getAttribute('data-variant-id')
          || ''
      );
      if (variantId) {
        toggle.setAttribute('data-variant-id', variantId);
      }
      const selectedVariantInCart =
        (variantId !== '' && cartVariantIds.has(variantId)) || satisfiedByAlternateVariant;
      toggle.checked = shouldShowUpsellAddedState(upsellList) && selectedVariantInCart;
      upsellItem.setAttribute('data-upsell-selected-in-cart', selectedVariantInCart ? 'true' : 'false');
      syncUpsellToggleLabel(upsellItem, upsellList, selectedVariantInCart);
    };

    const syncRecommendedUpsellItems = (cartState) => {
      if (cartState && Array.isArray(cartState.items)) {
        setLastKnownCartProductIds(cartState);
      }
      const cartProductIds = lastKnownCartProductIds;
      const cartVariantIds = lastKnownCartVariantIds;
      root.querySelectorAll('[data-upsell-item]').forEach((item) => {
        syncUpsellItemState(item, cartProductIds, cartVariantIds);
      });
    };

    const syncUpsellHeaderForList = (list, cartProductIds, extraProductIds = []) => {
      if (!list) return;
      const upsellContainer = list.closest('.kozler-cart-upsell');
      const upsellHeader = upsellContainer?.querySelector('[data-upsell-header]');
      if (!upsellHeader) return;

      const hideWhenInCart = list.dataset.hideHeaderWhenProductInCart === 'true';
      if (!hideWhenInCart) {
        upsellHeader.hidden = false;
        upsellHeader.setAttribute('aria-hidden', 'false');
        upsellHeader.style.display = '';
        return;
      }

      const alternateHeaderIds = filterBundleVariantIdList(parseIdCsv(list.dataset.alternateSatisfiedVariantIds || ''));
      if (alternateHeaderIds.some((id) => lastKnownCartVariantIds.has(String(id)))) {
        upsellHeader.hidden = true;
        upsellHeader.setAttribute('aria-hidden', 'true');
        upsellHeader.style.display = 'none';
        return;
      }

      let productIds = parseIdCsv(list.dataset.upsellProductIds);
      const renderedProductIds = Array.from(list.querySelectorAll('[data-upsell-product-id]'))
        .map((el) => el.getAttribute('data-upsell-product-id'))
        .filter(Boolean)
        .map((id) => String(id));
      const renderedSet = new Set(renderedProductIds);

      if (extraProductIds.length) {
        productIds = productIds.concat(extraProductIds.map((id) => String(id)));
      }
      const rememberedIds = getStoredUpsellProductIds(list).map((id) => String(id));
      const rememberedSet = new Set(rememberedIds);

      if (!productIds.length) {
        productIds = renderedProductIds.slice();
      }
      if (rememberedIds.length) {
        productIds = productIds.concat(rememberedIds);
      }

      const shouldHide = productIds.some((id) => {
        const normalized = String(id);
        const isRendered = renderedSet.has(normalized);
        const isInCart = cartProductIds.has(normalized);
        const isRemembered = rememberedSet.has(normalized);
        return !isRendered && (isInCart || isRemembered);
      });
      upsellHeader.hidden = shouldHide;
      upsellHeader.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
      upsellHeader.style.display = shouldHide ? 'none' : '';
    };

    // Hides the recommended-products equipment block when cart variant IDs match theme settings.
    // Those IDs must be program+equipment combo SKUs only — not standalone programs (Pilates, etc.),
    // which should still show this upsell. Merchant-facing copy is in the cart drawer block schema.
    const syncUpsellContainersForBundleVariants = () => {
      const resetRewardInCart = isResetRewardBundleInCart();
      root.querySelectorAll('.kozler-cart-upsell[data-hide-when-bundle-variant-ids]').forEach((container) => {
        const ids = filterBundleVariantIdList(parseIdCsv(container.getAttribute('data-hide-when-bundle-variant-ids') || ''));
        const hideFromConfiguredIds =
          ids.length > 0 && ids.some((id) => lastKnownCartVariantIds.has(String(id)));
        const hide = hideFromConfiguredIds || resetRewardInCart;
        if (hide) {
          container.hidden = true;
          container.style.display = 'none';
          container.setAttribute('aria-hidden', 'true');
        } else {
          container.hidden = false;
          container.style.removeProperty('display');
          container.removeAttribute('aria-hidden');
        }
      });
    };

    const syncRememberedUpsellHeaders = () => {
      root.querySelectorAll('.kozler-cart-upsell__list').forEach((list) => {
        syncUpsellHeaderForList(list, lastKnownCartProductIds);
      });
      syncRecommendedUpsellItems();
    };

    // Hide only dedicated one-click strip blocks (data-oneclick-upsell) when the same variant
    // already appears as a full cart line — avoids double UI. Does not touch smart/cross-sell
    // upsells (.kozler-cart-upsell__item), which reuse kozler-oneclick__* control styling only.
    const syncOneclickDuplicateVisibility = () => {
      const itemsRoot = content.querySelector('.kozler-cart__items');
      const oneClickBlocks = root.querySelectorAll('.kozler-oneclick[data-oneclick-upsell][data-variant-id]');
      if (!itemsRoot) {
        oneClickBlocks.forEach((el) => {
          el.hidden = false;
          el.removeAttribute('aria-hidden');
        });
        return;
      }
      const lineVariantIds = new Set(
        Array.from(itemsRoot.querySelectorAll('.kozler-cart-item[data-variant-id]'))
          .map((el) => el.getAttribute('data-variant-id'))
          .filter(Boolean),
      );
      oneClickBlocks.forEach((el) => {
        const vid = el.getAttribute('data-variant-id');
        const hide = Boolean(vid && lineVariantIds.has(vid));
        el.hidden = hide;
        if (hide) {
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.removeAttribute('aria-hidden');
        }
      });
    };

    const syncRecommendedUpsellHeaders = (cartState) => {
      const cartItems = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      const cartProductIds = new Set(cartItems.map((item) => String(item.product_id)));
      setLastKnownCartProductIds(cartState);
      root.querySelectorAll('.kozler-cart-upsell__list').forEach((list) => {
        if (shouldHideUpsellWhenAdded(list)) {
          const remembered = getStoredUpsellProductIds(list).map((id) => String(id));
          if (remembered.length) {
            const activeRemembered = remembered.filter((id) => cartProductIds.has(id));
            if (activeRemembered.length !== remembered.length) {
              setStoredUpsellProductIds(list, activeRemembered);
            }
          }
        } else {
          setStoredUpsellProductIds(list, []);
        }
        syncUpsellHeaderForList(list, cartProductIds);
      });
      syncRecommendedUpsellItems(cartState);
      syncUpsellContainersForBundleVariants();
    };

    const getCachedProducts = (key, fetcher, ttlMs = 120000) => {
      const cached = smartRecoCache.get(key);
      const now = Date.now();
      if (cached && now - cached.at < ttlMs) {
        return Promise.resolve(cached.products);
      }
      return fetcher().then((products) => {
        const normalized = Array.isArray(products) ? products.filter(Boolean) : [];
        smartRecoCache.set(key, { at: now, products: normalized });
        return normalized;
      });
    };

    const renderSmartUpsells = (cartState) => {
      const lists = root.querySelectorAll('[data-smart-recos="true"]');
      if (!lists.length || !cartState || !cartState.items) return;

      const cartProductIds = new Set(cartState.items.map((item) => String(item.product_id)));

      lists.forEach((list) => {
        const cardStyle = list.dataset.upsellCardStyle || 'compact';
        const controlStyle = getUpsellControlStyle(list);
        const labelSwitchesToAddedState = shouldUpsellLabelSwitchToAddedState(list);
        const limit = parseInt(list.dataset.limit || '4', 10);
        const hideQty = list.dataset.hideQty === 'true';
        const buttonLabel = list.dataset.buttonLabel || 'Add';
        const buttonShowPriceInLabel = list.dataset.buttonShowPriceInLabel === 'true';
        const hideWhenAdded = shouldHideUpsellWhenAdded(list);
        const showAddedState = shouldShowUpsellAddedState(list);
        const addedLabel = list.dataset.addedLabel || 'Added';
        const fallbackHandle = list.dataset.fallbackHandle;
        const imageSize = parseInt(list.dataset.imageSize || '120', 10);
        const imageSizeTraditional = parseInt(list.dataset.imageSizeTraditional || '800', 10);
        const imageWidth = cardStyle === 'traditional' ? Math.max(imageSizeTraditional, imageSize) : imageSize;
        const showVariantPrices = list.dataset.showVariantPrices === 'true';
        const seedIdAttr = list.dataset.seedProductId;
        const seedId = seedIdAttr && seedIdAttr !== '' ? seedIdAttr : (cartState.items[0]?.product_id);

        if (!seedId) {
          list.innerHTML = '';
          return;
        }

        const cartVariantIds = cartState.items.map((item) => item.variant_id);
        syncUpsellHeaderForList(list, cartProductIds);

        const pickVariant = (product) => {
          if (showAddedState && !hideWhenAdded) {
            const variantInCart = product.variants.find((v) => cartVariantIds.includes(v.id));
            if (variantInCart) return variantInCart;
          }
          let chosen = null;
          product.variants.forEach((v) => {
            if (chosen) return;
            const inCart = cartVariantIds.includes(v.id);
            if (v.available && !inCart) chosen = v;
          });
          if (!chosen) chosen = product.variants.find((v) => v.available) || product.variants[0];
          return chosen;
        };

        const buildCard = (product) => {
          const productInCart = cartProductIds.has(String(product.id));
          if (hideWhenAdded && productInCart) return '';
          const variant = pickVariant(product);
          if (!variant) return '';
          const variantInCart = cartVariantIds.includes(variant.id);

          const options = product.variants.map((v) => {
            const vInCart = cartVariantIds.includes(v.id);
            const shouldDisableInCartVariant = controlStyle === 'button' || !showAddedState || hideWhenAdded;
            const disabled = (!v.available || (vInCart && shouldDisableInCartVariant)) ? 'disabled' : '';
            const selected = v.id === variant.id ? 'selected' : '';
            const pricePart = showVariantPrices ? ` — ${formatMoneyOrFree(v.price)}` : '';
            return `<option value="${v.id}" data-price="${normalizeMoneyCents(v.price)}" data-compare-at-price="${normalizeMoneyCents(v.compare_at_price)}" ${selected} ${disabled}>${v.title}${pricePart}</option>`;
          }).join('');

          const qty = hideQty ? '' : `
            <div class="kozler-cart-item__controls kozler-cart-item__controls--inline">
              <div class="kozler-cart-quantity" data-upsell-qty>
                <button type="button" class="kozler-cart-quantity__btn" data-upsell-qty-button data-direction="down" aria-label="Decrease quantity">&minus;</button>
                <input class="kozler-cart-quantity__input" type="number" min="1" step="1" data-upsell-qty-input value="1" inputmode="numeric">
                <button type="button" class="kozler-cart-quantity__btn" data-upsell-qty-button data-direction="up" aria-label="Increase quantity">+</button>
              </div>
            </div>
          `;

          const imgSrc = (product.featured_image && product.featured_image.src) ? `${product.featured_image.src}&width=${imageWidth}` : (product.images && product.images.length ? `${product.images[0]}&width=${imageWidth}` : null);
          const actionLabel = getUpsellAddButtonLabel(buttonLabel, variant.price, buttonShowPriceInLabel);

          let actionMarkup = '';
          if (controlStyle === 'button') {
            if (!variant.available) {
              actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
            } else if (variantInCart && showAddedState) {
              actionMarkup = `<span class="kozler-cart-upsell__added">${escapeHtmlAttr(addedLabel)}</span>`;
            } else if (variantInCart) {
              actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
            } else {
              actionMarkup = `<button type="button" class="kozler-cart-upsell__add" data-upsell-add data-variant-id="${variant.id}" data-base-label="${escapeHtmlAttr(buttonLabel)}">${actionLabel}</button>`;
            }
          } else if (!variant.available) {
            actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
          } else {
            const checked = showAddedState && variantInCart;
            const checkedAttr = checked ? 'checked' : '';
            const useAddedLabel = checked && labelSwitchesToAddedState;
            const toggleLabel = useAddedLabel ? escapeHtmlAttr(addedLabel) : actionLabel;
            actionMarkup = `
              <div class="kozler-cart-upsell__toggle-row${useAddedLabel ? ' is-added' : ''}" data-upsell-toggle-wrap>
                <span class="kozler-cart-upsell__toggle-label" data-upsell-toggle-label data-base-label="${escapeHtmlAttr(buttonLabel)}" data-add-label="${escapeHtmlAttr(actionLabel)}" data-added-label="${escapeHtmlAttr(addedLabel)}">${toggleLabel}</span>
                <label class="kozler-oneclick__control kozler-oneclick__control--${controlStyle}">
                  <input type="checkbox" class="kozler-oneclick__input" data-upsell-toggle data-variant-id="${variant.id}" data-base-label="${escapeHtmlAttr(buttonLabel)}" aria-label="${escapeHtmlAttr(product.title)}" ${checkedAttr}>
                  <span class="kozler-oneclick__control-ui"></span>
                </label>
              </div>
            `;
          }

          return `
            <article class="kozler-cart-upsell__item" data-upsell-item data-upsell-product-id="${product.id}" data-upsell-product-in-cart="${productInCart ? 'true' : 'false'}" data-upsell-selected-in-cart="${variantInCart ? 'true' : 'false'}">
              <a href="${product.url}" class="kozler-cart-upsell__image">
                ${imgSrc ? `<img src="${imgSrc}" alt="${product.featured_image && product.featured_image.alt ? product.featured_image.alt : product.title}" class="kozler-cart-upsell__img" loading="lazy">` : '<div class="kozler-cart-upsell__placeholder" aria-hidden="true"></div>'}
              </a>
            <div class="kozler-cart-upsell__body">
              <p class="kozler-cart-upsell__title">${product.title}</p>
              <p class="kozler-cart-upsell__price" data-upsell-price>${getUpsellPriceMarkup(variant.price)}</p>
              <div class="kozler-cart-item__variant">
                ${product.variants.length > 1 ? `
                  <div class="kozler-cart-item__select-wrap">
                    <select class="kozler-cart-item__select" data-upsell-variant-select aria-label="Variant">
                      ${options}
                    </select>
                    <span class="kozler-cart-item__select-spinner" aria-hidden="true"></span>
                  </div>
                ` : ``}
                ${qty}
              </div>
            </div>
            <div class="kozler-cart-upsell__action">
              ${actionMarkup}
            </div>
          </article>
        `;
        };

        const renderList = (products) => {
          if (!products || !products.length) return false;
          const items = products.map(buildCard).filter(Boolean).slice(0, limit).join('');
          if (!items.length) return false;
          list.innerHTML = items;
          const renderedProductIds = Array.from(list.querySelectorAll('[data-upsell-product-id]'))
            .map((node) => node.getAttribute('data-upsell-product-id'))
            .filter(Boolean);
          syncUpsellHeaderForList(list, cartProductIds, renderedProductIds);
          syncRecommendedUpsellItems(cartState);
          return true;
        };

        const fetchRecommendations = (pid) =>
          getCachedProducts(
            `reco:${pid}:${limit}`,
            () =>
              fetch(`/recommendations/products.json?product_id=${pid}&limit=${limit}`)
                .then((res) => res.ok ? res.json() : null)
                .then((data) => data && data.products ? data.products : [])
          );

        const fetchCollection = (handle) =>
          getCachedProducts(
            `collection:${handle}:${limit}`,
            () =>
              fetch(`/collections/${handle}/products.json?limit=${limit}`)
                .then((res) => res.ok ? res.json() : null)
                .then((data) => data && data.products ? data.products : [])
          );

        fetchRecommendations(seedId)
          .then((prods) => {
            if (renderList(prods)) return;
            if (fallbackHandle) {
              return fetchCollection(fallbackHandle).then((colProds) => {
                if (renderList(colProds)) return;
                list.innerHTML = '';
                syncUpsellHeaderForList(list, cartProductIds);
              });
            }
            list.innerHTML = '';
            syncUpsellHeaderForList(list, cartProductIds);
          })
          .catch(() => {
            list.innerHTML = '';
            syncUpsellHeaderForList(list, cartProductIds);
          });
      });
    };

    const enforceGiftEligibility = (cartState) => {
      const giftBlocks = root.querySelectorAll('[data-free-shipping], [data-gift-selector]');
      if (!giftBlocks.length) return Promise.resolve();

      const ensureCart = cartState && cartState.items
        ? Promise.resolve(cartState)
        : fetch('/cart.js').then((res) => res.json());

      return ensureCart
        .then((state) => {
          if (!state || !state.items) return [];
          const removals = [];
          const queuedRemovals = new Set();

          const collectGiftRules = (block) => {
            const explicitRules = Array.from(block.querySelectorAll('[data-gift-rule]'))
              .map((node) => ({
                variant: String(node.getAttribute('data-gift-variant') || ''),
                threshold: parseInt(node.getAttribute('data-gift-threshold') || '0', 10),
              }))
              .filter((rule) => rule.variant !== '' && Number.isFinite(rule.threshold) && rule.threshold > 0);
            if (explicitRules.length) return explicitRules;

            const legacyRules = [1, 2]
              .map((index) => ({
                variant: String(block.getAttribute(`data-gift-variant-${index}`) || ''),
                threshold: parseInt(block.getAttribute(`data-gift-threshold-${index}`) || '0', 10),
              }))
              .filter((rule) => rule.variant !== '' && Number.isFinite(rule.threshold) && rule.threshold > 0);
            return legacyRules;
          };

          giftBlocks.forEach((block) => {
            const rules = collectGiftRules(block);
            if (!rules.length) return;
            const thresholdByVariant = new Map();
            rules.forEach((rule) => {
              thresholdByVariant.set(rule.variant, rule.threshold);
            });
            const giftVariants = Array.from(thresholdByVariant.keys());

            const cartSubtotal = state.items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
            const giftsTotal = state.items
              .filter((item) => giftVariants.includes(String(item.id)))
              .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
            const effectiveTotal = cartSubtotal - giftsTotal;

            state.items
              .filter((item) => giftVariants.includes(String(item.id)))
              .forEach((item) => {
                const required = thresholdByVariant.get(String(item.id)) || 0;
                if (required > 0 && effectiveTotal < required && !queuedRemovals.has(item.key)) {
                  queuedRemovals.add(item.key);
                  removals.push(updateQuantity(item.key, 0, null));
                }
              });
          });

          return Promise.all(removals.filter(Boolean));
        })
        .catch((err) => {
          console.error('Gift enforcement failed', err);
          return [];
        });
    };

    const initCountdowns = () => {
      const parseTargetDate = (rawValue) => {
        if (!rawValue) return null;
        const value = String(rawValue).trim();
        if (!value) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const parts = value.split('-').map((part) => parseInt(part, 10));
          if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
          const [year, month, day] = parts;
          return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
        }

        const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
        const timestamp = parsed.getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
      };

      const formatDurationTime = (remainingSeconds) => {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return `${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
      };

      const formatTargetTime = (remainingSeconds) => {
        const total = Math.max(0, remainingSeconds);
        const days = Math.floor(total / 86400);
        const hours = Math.floor((total % 86400) / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        if (days > 0) {
          return `${days}d : ${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
        }
        return `${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
      };

      const timers = root.querySelectorAll('[data-countdown]');
      timers.forEach((el) => {
        const mode = el.dataset.countdownMode === 'date' ? 'date' : 'duration';
        const duration = parseInt(el.dataset.duration || '0', 10);
        const shouldReset = el.dataset.reset === 'true';
        const completeText = el.dataset.completeText || '';
        const targetDate = parseTargetDate(el.dataset.targetDate);
        const display = el.querySelector('[data-countdown-display]');
        const hasDuration = duration > 0;
        const hasTargetDate = Number.isFinite(targetDate);
        if (!display) return;

        let remaining = duration;
        if (el._countdownInterval) {
          clearInterval(el._countdownInterval);
        }

        if ((mode === 'duration' && !hasDuration) || (mode === 'date' && !hasTargetDate)) {
          if (completeText !== '') {
            display.textContent = completeText;
          }
          return;
        }

        const tick = () => {
          if (mode === 'date') {
            const secondsLeft = Math.floor((targetDate - Date.now()) / 1000);
            if (secondsLeft <= 0) {
              if (completeText !== '') {
                display.textContent = completeText;
              } else {
                display.textContent = '00h : 00m : 00s';
              }
              clearInterval(el._countdownInterval);
              return;
            }
            display.textContent = formatTargetTime(secondsLeft);
            return;
          }

          display.textContent = formatDurationTime(remaining);
          remaining -= 1;

          if (remaining < 0) {
            if (shouldReset) {
              remaining = duration;
            } else {
              if (completeText !== '') {
                display.textContent = completeText;
              }
              clearInterval(el._countdownInterval);
            }
          }
        };

        tick();
        el._countdownInterval = setInterval(tick, 1000);
      });
    };

    let refreshPromise = null;
    const LINE_MISMATCH_MAX_ATTEMPTS = 3;
    const LINE_MISMATCH_RETRY_MS = 60;
    const EMPTY_CART_RETRY_MS = 120;

    const refreshDrawer = (attempt = 0) => {
      if (attempt === 0 && refreshPromise) {
        return refreshPromise;
      }

      const runRefresh = () =>
        Promise.all([
          fetch(getSectionEndpoint())
            .then((response) => response.text())
            .catch(() => ''),
          fetch('/cart.js')
            .then((res) => res.json())
            .catch(() => null),
        ]).then(([html, cartState]) => {
          if (!cartState) {
            return Promise.resolve();
          }

          setLastKnownCartProductIds(cartState);

          const itemCount = cartState?.item_count || 0;
          const cartLineCount = Array.isArray(cartState?.items) ? cartState.items.length : 0;

          updateHeaderActions(itemCount);

          const parser = new DOMParser();
          const doc = parser.parseFromString(html || '', 'text/html');
          const updatedContent = doc.querySelector(`#kozler-cart-${sectionId} [data-cart-content]`);
          const updatedIsEmpty =
            !!updatedContent &&
            (updatedContent.classList.contains('kozler-cart__content--empty') ||
              !!updatedContent.querySelector('.kozler-cart__empty'));
          const sectionLineCount = updatedContent
            ? updatedContent.querySelectorAll('.kozler-cart__items .kozler-cart-item').length
            : 0;

          if (itemCount === 0 && !updatedIsEmpty && attempt < 2) {
            return new Promise((resolve) => {
              setTimeout(resolve, EMPTY_CART_RETRY_MS);
            }).then(() => refreshDrawer(attempt + 1));
          }

          if (
            cartLineCount > 0 &&
            cartLineCount > sectionLineCount &&
            attempt < LINE_MISMATCH_MAX_ATTEMPTS
          ) {
            return new Promise((resolve) => {
              setTimeout(resolve, LINE_MISMATCH_RETRY_MS);
            }).then(() => refreshDrawer(attempt + 1));
          }

          if (updatedContent && content) {
            content.innerHTML = updatedContent.innerHTML;
            content.className = updatedContent.className;
            Array.from(updatedContent.attributes).forEach((attr) => {
              if (attr.name.startsWith('data-')) {
                content.setAttribute(attr.name, attr.value);
              }
            });
            syncRememberedUpsellHeaders();
            syncOneclickDuplicateVisibility();
          }

          updateTermsState();
          initCountdowns();
          renderSmartUpsells(cartState);
          syncRecommendedUpsellHeaders(cartState);
          syncOneclickDuplicateVisibility();
          const autoAddPromise = enforceAutoAddRules(cartState);
          const giftPromise = enforceGiftEligibility(cartState);
          document.dispatchEvent(new CustomEvent('kozler:cart:refreshed', { detail: { count: itemCount, cart: cartState } }));
          return Promise.all([autoAddPromise, giftPromise].filter(Boolean));
        });

      const request = runRefresh().catch((error) => {
        console.error('Cart refresh failed', error);
      });

      if (attempt === 0) {
        setBusy(true);
        root.classList.add('kozler-cart--refreshing');
        root.setAttribute('aria-busy', 'true');
        refreshPromise = request.finally(() => {
          setBusy(false);
          root.classList.remove('kozler-cart--refreshing');
          root.removeAttribute('aria-busy');
          refreshPromise = null;
        });
        return refreshPromise;
      }

      return request;
    };

    const updateQuantity = (lineKey, quantity, controlEl) => {
      if (!lineKey) return;
      const nextQuantity = Math.max(0, quantity);
      if (nextQuantity === 0 && controlEl) {
        const parentLine = controlEl.closest ? controlEl.closest('[data-cart-line]') : null;
        const variantId = parentLine?.getAttribute('data-variant-id');
        if (variantId) {
          suppressAutoAddForVariant(variantId);
        }
      }
      startControlLoading(controlEl);
      setBusy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: nextQuantity }),
      })
        .then(() => refreshDrawer())
        .catch((error) => {
          console.error('Quantity update failed', error);
        })
        .finally(() => {
          stopControlLoading(controlEl);
          setBusy(false);
        });
    };

    /* PDP bundle chooser patches fetch(); cart drawer adds must bypass that intercept. */
    const fetchCartAddJsWithBundleChooserBypass = (init) => {
      const prev = window._bypassBundleChooser;
      window._bypassBundleChooser = true;
      return fetch('/cart/add.js', init).finally(() => {
        window._bypassBundleChooser = prev;
      });
    };

    const addVariant = (variantId, controlEl, quantity = 1, options = {}) => {
      if (!variantId) return;
      const shouldRefresh = options.refresh !== false;
      const onFailure = typeof options.onFailure === 'function' ? options.onFailure : null;
      const loadingTarget = resolveControlLoadingTarget(controlEl);
      startControlLoading(loadingTarget);
      setBusy(true);
      return fetchCartAddJsWithBundleChooserBypass({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: Math.max(1, quantity) }),
      })
        .then(() => {
          if (shouldRefresh) {
            return refreshDrawer();
          }
        })
        .catch((error) => {
          console.error('Add to cart failed', error);
          if (onFailure) {
            onFailure(error);
          }
        })
        .finally(() => {
          stopControlLoading(loadingTarget);
          setBusy(false);
        });
    };

    const collectUpsellVariantIdsFromList = (listEl) => {
      const ids = new Set();
      if (!listEl) return ids;
      listEl.querySelectorAll('[data-upsell-toggle][data-variant-id], [data-upsell-add][data-variant-id]').forEach((el) => {
        const vid = el.getAttribute('data-variant-id');
        if (vid) ids.add(String(vid));
      });
      listEl.querySelectorAll('[data-upsell-variant-select] option[value]').forEach((opt) => {
        const vid = opt.getAttribute('value');
        if (vid) ids.add(String(vid));
      });
      return ids;
    };

    // Suppresses auto-add of upsell targets when the cart already has a combo variant listed in
    // data-alternate-satisfied-variant-ids (combo = program+equipment), not program-only lines.
    const shouldSuppressAutoAddForAlternateBundle = (targetVariantId, cartVariantIds) => {
      const targetStr = String(targetVariantId);
      let suppressed = false;
      root.querySelectorAll('.kozler-cart-upsell__list').forEach((list) => {
        const alternates = filterBundleVariantIdList(parseIdCsv(list.dataset.alternateSatisfiedVariantIds || ''));
        if (!alternates.some((id) => cartVariantIds.includes(String(id)))) return;
        if (collectUpsellVariantIdsFromList(list).has(targetStr)) suppressed = true;
      });
      return suppressed;
    };

    const enforceAutoAddRules = (cartState) => {
      const rules = collectAutoAddRules();
      if (!rules.length) return;

      const ensureCart = cartState && cartState.items ? Promise.resolve(cartState) : fetch('/cart.js').then((res) => res.json());

      return ensureCart
        .then((state) => {
          if (!state || !state.items) return;
          loadSuppressedRules(state);
          const variantIds = state.items.map((item) => String(item.variant_id));
          const actions = [];
          let suppressedChanged = false;
          const rulesByTarget = new Map();

          rules.forEach((rule) => {
            if (!variantIds.includes(rule.trigger) && autoAddSuppressed.has(rule.id)) {
              autoAddSuppressed.delete(rule.id);
              suppressedChanged = true;
            }
            const targetKey = String(rule.target);
            if (!rulesByTarget.has(targetKey)) {
              rulesByTarget.set(targetKey, []);
            }
            rulesByTarget.get(targetKey).push(rule);
          });

          if (suppressedChanged) {
            updateCartAttributes({ [AUTO_ADD_SUPPRESS_ATTR]: Array.from(autoAddSuppressed).join(',') });
          }

          rulesByTarget.forEach((targetRules, targetVariantId) => {
            const targetInCart = variantIds.includes(targetVariantId);
            const activeTriggerRules = targetRules.filter((rule) => variantIds.includes(rule.trigger));

            // If no triggers for this target are currently in cart, remove the auto-added target.
            if (!activeTriggerRules.length) {
              if (targetInCart) {
                actions.push(() => removeVariant(Number(targetVariantId), null, { refresh: false }));
              }
              return;
            }

            if (targetInCart) return;

            if (shouldSuppressAutoAddForAlternateBundle(targetVariantId, variantIds)) return;

            const eligibleRules = activeTriggerRules.filter((rule) => !autoAddSuppressed.has(rule.id));
            if (!eligibleRules.length) return;

            const desiredQty = eligibleRules.reduce((maxQty, rule) => {
              const nextQty = Math.max(1, parseInt(rule.qty, 10) || 1);
              return Math.max(maxQty, nextQty);
            }, 1);

            actions.push(() => addVariant(Number(targetVariantId), null, desiredQty, { refresh: false }));
          });

          if (!actions.length) return;

          return actions
            .reduce((promise, runAction) => promise.then(() => runAction()), Promise.resolve())
            .then(() => refreshDrawer());
        })
        .catch((error) => console.error('Auto-add rules failed', error));
    };

    const removeVariant = (variantId, controlEl, options = {}) => {
      if (!variantId) return;
      const shouldRefresh = options.refresh !== false;
      const onFailure = typeof options.onFailure === 'function' ? options.onFailure : null;
      const loadingTarget = resolveControlLoadingTarget(controlEl);
      startControlLoading(loadingTarget);
      setBusy(true);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: 0 } }),
      })
        .then(() => {
          if (shouldRefresh) {
            return refreshDrawer();
          }
        })
        .catch((error) => {
          console.error('Remove variant failed', error);
          if (onFailure) {
            onFailure(error);
          }
        })
        .finally(() => {
          stopControlLoading(loadingTarget);
          setBusy(false);
        });
    };

    const removeLine = (lineKey, controlEl) => updateQuantity(lineKey, 0, controlEl);

    const swapVariant = (lineKey, nextId, quantity, sellingPlan, properties, controlEl) => {
      if (!lineKey || !nextId) return;
      setBusy(true);
      startControlLoading(controlEl);

      if (properties && typeof properties === 'object') {
        Object.keys(properties).forEach((key) => {
          if (properties[key] === '' || properties[key] === null) {
            delete properties[key];
          }
        });
      }

      const addPayload = { id: nextId, quantity };
      if (sellingPlan) addPayload.selling_plan = sellingPlan;
      if (properties && Object.keys(properties).length > 0) addPayload.properties = properties;

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      })
        .then(() =>
          fetchCartAddJsWithBundleChooserBypass({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(addPayload),
          })
        )
        .then(() => refreshDrawer())
        .catch((error) => {
          console.error('Variant change failed', error);
        })
        .finally(() => {
          stopControlLoading(controlEl);
          setBusy(false);
        });
    };

    const updateNote = (note) => {
      setBusy(true);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      })
        .then(() => refreshDrawer())
        .catch((error) => {
          console.error('Cart note failed', error);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const getCartAttributeValue = (fieldBlock) => {
      if (!fieldBlock) return '';
      const fieldType = fieldBlock.dataset.fieldType || 'text';
      if (fieldType === 'checkbox') {
        const checked = Array.from(fieldBlock.querySelectorAll('input[type="checkbox"][data-cart-attribute-input]:checked'))
          .map((input) => input.value.trim())
          .filter(Boolean);
        return checked.length ? checked.join(' | ') : '';
      }
      if (fieldType === 'radio') {
        const checked = fieldBlock.querySelector('input[type="radio"][data-cart-attribute-input]:checked');
        return checked ? checked.value.trim() : '';
      }
      const input = fieldBlock.querySelector('[data-cart-attribute-input]');
      return input ? input.value.trim() : '';
    };

    const updateCartAttributeField = (fieldBlock) => {
      if (!fieldBlock) return;
      const attributeKey = fieldBlock.dataset.attributeKey;
      if (!attributeKey) return;
      const nextValue = getCartAttributeValue(fieldBlock);
      startControlLoading(fieldBlock);
      return updateCartAttributes({ [attributeKey]: nextValue })
        .finally(() => {
          stopControlLoading(fieldBlock);
        });
    };

    const validateCartAttributeFields = () => {
      const fields = content.querySelectorAll('[data-cart-attribute-field]');
      if (!fields.length) return true;
      let firstInvalid = null;
      fields.forEach((field) => {
        const required = field.dataset.required === 'true';
        if (!required) return;
        const value = getCartAttributeValue(field);
        const error = field.querySelector('[data-cart-attribute-error]');
        if (!value) {
          if (error) error.hidden = false;
          field.classList.add('has-error');
          if (!firstInvalid) firstInvalid = field;
          return;
        }
        if (error) error.hidden = true;
        field.classList.remove('has-error');
      });
      if (firstInvalid) {
        const focusEl = firstInvalid.querySelector('[data-cart-attribute-input]');
        if (focusEl) focusEl.focus();
        return false;
      }
      return true;
    };

    const collapseMinimalQtyControls = (exceptEl = null) => {
      const expandedControls = content.querySelectorAll('[data-qty-minimal].is-expanded');
      expandedControls.forEach((control) => {
        if (exceptEl && (control === exceptEl || control.contains(exceptEl))) return;
        control.classList.remove('is-expanded');
        control.setAttribute('data-qty-expanded', 'false');
      });
    };

    const expandMinimalQtyControl = (control) => {
      if (!control) return;
      collapseMinimalQtyControls(control);
      control.classList.add('is-expanded');
      control.setAttribute('data-qty-expanded', 'true');
    };

    content.addEventListener('click', (event) => {
      const qtyButton = closestFromEvent(event,'[data-qty-button]');
      const removeButton = closestFromEvent(event,'[data-remove-line]');
      const upsellQtyButton = closestFromEvent(event,'[data-upsell-qty-button]');
      const breakToggle = closestFromEvent(event,'[data-quantity-break-toggle]');
      const minimalQtyControl = closestFromEvent(event,'[data-qty-minimal]');

      if (minimalQtyControl && !minimalQtyControl.classList.contains('is-expanded')) {
        const clickedQtyButton = !!closestFromEvent(event,'[data-qty-button]');
        if (!clickedQtyButton) {
          event.preventDefault();
          expandMinimalQtyControl(minimalQtyControl);
          const qtyInput = minimalQtyControl.querySelector('[data-qty-input]');
          if (qtyInput) {
            qtyInput.focus({ preventScroll: true });
            if (typeof qtyInput.select === 'function') qtyInput.select();
          }
          return;
        }
      } else if (!minimalQtyControl) {
        collapseMinimalQtyControls();
      }

      if (qtyButton) {
        const parentLine = qtyButton.closest('[data-cart-line]');
        const input = parentLine?.querySelector('[data-qty-input]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey || !input) return;
        const qtyControl = qtyButton.closest('[data-qty-minimal]');
        if (qtyControl) {
          expandMinimalQtyControl(qtyControl);
        }

        const currentQty = parseInt(input.value, 10) || 0;
        const nextQty = qtyButton.dataset.direction === 'up' ? currentQty + 1 : Math.max(0, currentQty - 1);
        updateQuantity(lineKey, nextQty, qtyButton);
        return;
      }

      if (upsellQtyButton) {
        const qtyWrapper = upsellQtyButton.closest('[data-upsell-qty]');
        const input = qtyWrapper?.querySelector('[data-upsell-qty-input]');
        if (!input) return;
        const currentQty = parseInt(input.value, 10) || 1;
        const nextQty = upsellQtyButton.dataset.direction === 'up' ? currentQty + 1 : Math.max(1, currentQty - 1);
        input.value = nextQty;
        return;
      }

      if (breakToggle) {
        const parentLine = breakToggle.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;

        const baseRaw = breakToggle.closest('[data-quantity-breaks]')?.getAttribute('data-break-base-qty') || '1';
        const baseQty = parseInt(baseRaw, 10);
        const defaultQty = Number.isFinite(baseQty) ? Math.max(1, baseQty) : 1;
        const breakQtyRaw = breakToggle.getAttribute('data-break-qty');
        const breakQty = parseInt(breakQtyRaw, 10);
        if (!Number.isFinite(breakQty) || breakQty < 1) return;

        const isActive = breakToggle.classList.contains('is-active') || breakToggle.getAttribute('aria-pressed') === 'true';
        const nextQty = isActive ? defaultQty : breakQty;
        const qtyInputEl = parentLine.querySelector('[data-qty-input]');
        const currentQty = qtyInputEl ? parseInt(qtyInputEl.value, 10) : null;
        if (currentQty === nextQty) return;

        updateQuantity(lineKey, nextQty, breakToggle);
        return;
      }

      if (removeButton) {
        const lineKey = removeButton.getAttribute('data-line-key') || removeButton.closest('[data-cart-line]')?.getAttribute('data-line-key');
        if (!lineKey) return;
        removeLine(lineKey, removeButton);
      }
    });

    content.addEventListener('submit', (event) => {
      const form = closestFromEvent(event,'.kozler-cart__summary-form');
      if (!form) return;
      if (busyCount > 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const validNote = validateNoteRequirement();
      const validTerms = validateTermsRequirement();
      const validFields = validateCartAttributeFields();
      if (!validNote || !validTerms || !validFields) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    content.addEventListener('change', (event) => {
      const termsCheckbox = closestFromEvent(event,'[data-terms-checkbox]');
      const attributeInput = closestFromEvent(event,'[data-cart-attribute-input]');
      const breakOption = closestFromEvent(event,'[data-quantity-break-option]');
      const qtyInput = closestFromEvent(event,'[data-qty-input]');
      const upsellQtyInput = closestFromEvent(event,'[data-upsell-qty-input]');
      const variantSelect = closestFromEvent(event,'[data-variant-select]');
      const upsellVariantSelect = closestFromEvent(event,'[data-upsell-variant-select]');
      const upsellToggle = closestFromEvent(event,'[data-upsell-toggle]');

      if (termsCheckbox) {
        const termsBlock = termsCheckbox.closest('[data-terms-block]');
        const error = termsBlock?.querySelector('[data-terms-error]');
        if (error) error.hidden = true;
        termsBlock?.classList.remove('has-error');
        updateTermsState();
        return;
      }

      if (attributeInput) {
        const fieldBlock = attributeInput.closest('[data-cart-attribute-field]');
        if (!fieldBlock) return;
        const error = fieldBlock.querySelector('[data-cart-attribute-error]');
        if (error) error.hidden = true;
        fieldBlock.classList.remove('has-error');
        updateCartAttributeField(fieldBlock);
        return;
      }

      if (breakOption) {
        const parentLine = breakOption.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;
        const rawQty = breakOption.getAttribute('data-break-qty') || breakOption.value;
        const nextQty = parseInt(rawQty, 10);
        if (!Number.isFinite(nextQty) || nextQty < 0) return;
        const qtyInputEl = parentLine?.querySelector('[data-qty-input]');
        const currentQty = qtyInputEl ? parseInt(qtyInputEl.value, 10) : null;
        if (currentQty === nextQty) return;
        const breakLabel = breakOption.closest('.kozler-cart-item__break') || breakOption;
        updateQuantity(lineKey, nextQty, breakLabel);
        return;
      }

      if (qtyInput) {
        const parentLine = qtyInput.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;
        const minimalControl = qtyInput.closest('[data-qty-minimal]');
        if (minimalControl) {
          expandMinimalQtyControl(minimalControl);
        }
        const fallbackRaw = parseInt(qtyInput.getAttribute('value'), 10);
        const fallbackQty = Number.isFinite(fallbackRaw) ? fallbackRaw : 1;
        const parsed = parseInt(qtyInput.value, 10);
        if (!Number.isFinite(parsed)) {
          qtyInput.value = String(fallbackQty);
          return;
        }
        const nextQty = Math.max(0, parsed);
        qtyInput.value = String(nextQty);
        if (nextQty === fallbackQty) return;
        const qtyWrapper = qtyInput.closest('.kozler-cart-quantity') || qtyInput;
        updateQuantity(lineKey, nextQty, qtyWrapper);
        return;
      }

      if (upsellQtyInput) {
        const parsed = parseInt(upsellQtyInput.value, 10);
        const nextQty = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
        upsellQtyInput.value = String(nextQty);
        return;
      }

      if (upsellToggle) {
        const upsellItem = upsellToggle.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const variantSelect = upsellItem?.querySelector('[data-upsell-variant-select]');
        const qtyInput = upsellItem?.querySelector('[data-upsell-qty-input]');
        const variantId = variantSelect ? variantSelect.value : upsellToggle.getAttribute('data-variant-id');
        const productId = upsellItem?.getAttribute('data-upsell-product-id');
        if (!variantId) {
          upsellToggle.checked = false;
          return;
        }

        if (upsellToggle.checked) {
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
          if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
            rememberUpsellProductId(upsellList, productId);
            syncUpsellHeaderForList(upsellList, new Set([String(productId)]));
          }
          addVariant(Number(variantId), upsellToggle, qty, {
            onFailure: () => {
              upsellToggle.checked = false;
              if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
                forgetUpsellProductId(upsellList, productId);
                syncUpsellHeaderForList(upsellList, new Set());
              }
              syncUpsellItemState(upsellItem);
            },
          });
        } else {
          removeVariant(Number(variantId), upsellToggle, {
            onFailure: () => {
              upsellToggle.checked = true;
              syncUpsellItemState(upsellItem);
            },
          });
        }
        syncUpsellItemState(upsellItem);
        return;
      }

      if (variantSelect) {
        const parentLine = variantSelect.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        const currentVariant = parentLine?.getAttribute('data-variant-id');
        const qtyInput = parentLine?.querySelector('[data-qty-input]');
        if (!lineKey || !qtyInput) return;

        const quantity = parseInt(qtyInput.value, 10) || 1;
        const nextVariantId = parseInt(variantSelect.value, 10);
        if (!nextVariantId) return;
        if (String(currentVariant) === String(nextVariantId)) return;

        const sellingPlan = parentLine?.getAttribute('data-selling-plan') || null;
        let properties = null;

        if (parentLine?.dataset.lineProperties) {
          try {
            properties = JSON.parse(parentLine.dataset.lineProperties);
          } catch (error) {
            properties = null;
          }
        }

        swapVariant(lineKey, nextVariantId, quantity, sellingPlan, properties, variantSelect);
        return;
      }

      if (upsellVariantSelect) {
        const upsellItem = upsellVariantSelect.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const addButton = upsellItem?.querySelector('[data-upsell-add]');
        const toggleInput = upsellItem?.querySelector('[data-upsell-toggle]');
        if (addButton) {
          addButton.setAttribute('data-variant-id', upsellVariantSelect.value);
        }
        if (toggleInput) {
          toggleInput.setAttribute('data-variant-id', upsellVariantSelect.value);
        }
        const selectedOption = upsellVariantSelect.options[upsellVariantSelect.selectedIndex];
        if (selectedOption) {
          syncUpsellPrice(upsellItem, selectedOption.getAttribute('data-price'));
          if (addButton) {
            const showPriceInLabel = upsellList?.dataset.buttonShowPriceInLabel === 'true';
            const baseLabel = addButton.getAttribute('data-base-label') || upsellList?.dataset.buttonLabel || 'Add';
            addButton.textContent = getUpsellAddButtonLabel(baseLabel, selectedOption.getAttribute('data-price'), showPriceInLabel);
          }
          if (toggleInput && upsellList) {
            const toggleLabel = upsellItem.querySelector('[data-upsell-toggle-label]');
            if (toggleLabel) {
              const baseLabel = toggleLabel.getAttribute('data-base-label') || upsellList.dataset.buttonLabel || 'Add';
              const showPriceInLabel = upsellList.dataset.buttonShowPriceInLabel === 'true';
              const nextAddLabel = getUpsellAddButtonLabel(baseLabel, selectedOption.getAttribute('data-price'), showPriceInLabel);
              toggleLabel.setAttribute('data-add-label', nextAddLabel);
            }
            syncUpsellItemState(upsellItem);
          }
        }
        return;
      }
    });

    content.addEventListener('input', (event) => {
      const noteField = closestFromEvent(event,'[data-cart-note]');
      if (noteField) {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(() => updateNote(noteField.value), 400);
      }
    });

    content.addEventListener('focusin', (event) => {
      const qtyInput = closestFromEvent(event,'[data-qty-input]');
      if (!qtyInput) return;
      const minimalControl = qtyInput.closest('[data-qty-minimal]');
      if (!minimalControl) return;
      expandMinimalQtyControl(minimalControl);
    });

    content.addEventListener('click', (event) => {
      const noteToggle = closestFromEvent(event,'[data-note-toggle]');
      const checkoutButton = closestFromEvent(event,'[data-checkout-button]');
      const tooltipTrigger = closestFromEvent(event,'[data-terms-tooltip-trigger]');

      if (tooltipTrigger) {
        event.preventDefault();
        event.stopPropagation();
        const tooltip = tooltipTrigger.closest('[data-terms-tooltip]');
        if (!tooltip) return;
        const isOpen = tooltip.classList.toggle('is-open');
        tooltipTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        return;
      }

      if (noteToggle) {
        const noteBlock = noteToggle.closest('[data-note-block]');
        const body = noteBlock?.querySelector('.kozler-cart__note-body');
        if (!body) return;
        const isHidden = body.hasAttribute('hidden');
        if (isHidden) {
          body.removeAttribute('hidden');
          noteToggle.setAttribute('aria-expanded', 'true');
        } else {
          body.setAttribute('hidden', '');
          noteToggle.setAttribute('aria-expanded', 'false');
        }
        return;
      }

      if (checkoutButton) {
        if (busyCount > 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const validNote = validateNoteRequirement();
        const validTerms = validateTermsRequirement();
        const validFields = validateCartAttributeFields();
        if (!validNote || !validTerms || !validFields) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    });

    if (shareButton) {
      shareButton.addEventListener('click', (event) => {
        event.preventDefault();
        shareCart();
      });
    }
    if (clearButton) {
      clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        clearCart();
      });
    }

    initCountdowns();
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cartState) => {
        setLastKnownCartProductIds(cartState);
        updateHeaderActions(cartState?.item_count || 0);
        renderSmartUpsells(cartState);
        syncRecommendedUpsellHeaders(cartState);
        syncOneclickDuplicateVisibility();
        enforceAutoAddRules(cartState);
        return enforceGiftEligibility(cartState);
      })
      .catch(() => {});

    content.addEventListener('click', (event) => {
      const giftChoiceToggle = closestFromEvent(event,'[data-gift-choice-toggle]');
      const giftChoiceClose = closestFromEvent(event,'[data-gift-choice-close]');
      const giftButton = closestFromEvent(event,'[data-free-gift-add]');
      const upsellButton = closestFromEvent(event,'[data-upsell-add]');
      const oneClickToggle = closestFromEvent(event,'[data-oneclick-toggle]');

      if (giftChoiceToggle) {
        event.preventDefault();
        const selector = giftChoiceToggle.closest('[data-gift-selector]');
        if (!selector) return;
        const choiceDisplay = selector.getAttribute('data-gift-choice-display') || 'dropdown';
        const choiceCard = giftChoiceToggle.closest('.kozler-gift-selector__item--choice');
        let isOpen = false;

        if (choiceDisplay === 'inline') {
          if (!choiceCard) return;
          isOpen = !choiceCard.classList.contains('is-choice-open');
          choiceCard.classList.toggle('is-choice-open', isOpen);
        } else {
          const panel = selector.querySelector('[data-gift-choice-panel]');
          if (!panel) return;
          isOpen = panel.classList.toggle('is-open');
        }

        const toggles = selector.querySelectorAll('[data-gift-choice-toggle]');
        toggles.forEach((toggleEl) => {
          toggleEl.classList.toggle('is-open', isOpen);
          toggleEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
        return;
      }

      if (giftChoiceClose) {
        event.preventDefault();
        const selector = giftChoiceClose.closest('[data-gift-selector]');
        if (!selector) return;
        const choiceDisplay = selector.getAttribute('data-gift-choice-display') || 'dropdown';

        if (choiceDisplay === 'inline') {
          const choiceCard = giftChoiceClose.closest('.kozler-gift-selector__item--choice');
          if (choiceCard) {
            choiceCard.classList.remove('is-choice-open');
          }
        } else {
          const panel = selector.querySelector('[data-gift-choice-panel]');
          panel?.classList.remove('is-open');
        }

        const toggles = selector.querySelectorAll('[data-gift-choice-toggle]');
        toggles.forEach((toggleEl) => {
          toggleEl.classList.remove('is-open');
          toggleEl.setAttribute('aria-expanded', 'false');
        });
        return;
      }

      if (giftButton) {
        event.preventDefault();
        const variantId = giftButton.getAttribute('data-gift-variant');
        if (!variantId) return;
        addVariant(Number(variantId), giftButton, 1);
        return;
      }
      if (upsellButton) {
        event.preventDefault();
        const upsellItem = upsellButton.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const variantSelect = upsellItem?.querySelector('[data-upsell-variant-select]');
        const qtyInput = upsellItem?.querySelector('[data-upsell-qty-input]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const variantId = variantSelect ? variantSelect.value : upsellButton.getAttribute('data-variant-id');
        const productId = upsellItem?.getAttribute('data-upsell-product-id');
        if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
          rememberUpsellProductId(upsellList, productId);
          syncUpsellHeaderForList(upsellList, new Set([String(productId)]));
        }
        if (!variantId) return;
        addVariant(Number(variantId), upsellButton, qty, {
          onFailure: () => {
            if (!upsellList || !productId || !shouldHideUpsellWhenAdded(upsellList)) return;
            forgetUpsellProductId(upsellList, productId);
            syncUpsellHeaderForList(upsellList, new Set());
          },
        });
        return;
      }
      if (oneClickToggle) {
        const checked = oneClickToggle.checked;
        const variantId = oneClickToggle.closest('[data-oneclick-upsell]')?.getAttribute('data-variant-id');
        if (!variantId) return;
        if (checked) {
          addVariant(Number(variantId), oneClickToggle, 1);
        } else {
          removeVariant(Number(variantId), oneClickToggle);
        }
        return;
      }
    });

    const OPEN_STYLE_GROW = 'grow';
    let closeTimer = null;

    const isGrowStyle = () => root.dataset.openStyle === OPEN_STYLE_GROW;

    const getDuration = (value, fallback) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const getGrowDuration = () => getDuration(root.dataset.growDuration, 320);
    const getFadeDuration = () => getDuration(root.dataset.fadeDuration, 180);

    const lockPage = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      document.documentElement.classList.add('kozler-cart-locked');
      document.body.classList.add('kozler-cart-locked');
      document.body.style.top = `-${scrollY}px`;
      root.dataset.scrollY = String(scrollY);
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('kozler-cart-locked');
      document.body.classList.remove('kozler-cart-locked');
      document.documentElement.classList.remove('js-drawer-open');
      document.body.style.top = '';
      const scrollY = parseInt(root.dataset.scrollY || '0', 10) || 0;
      window.scrollTo(0, scrollY);
      root.dataset.scrollY = '';
    };

    const openDrawer = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      root.classList.remove('is-closing');
      collapseMinimalQtyControls();
      lockPage();
      root.classList.add('is-open');
    };

    const closeDrawer = () => {
      const isOpen = root.classList.contains('is-open');
      const isLocked = document.documentElement.classList.contains('kozler-cart-locked');
      if (!isOpen && !isLocked) return;
      if (!isOpen) {
        unlockPage();
        return;
      }

      if (isGrowStyle()) {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
        }
        root.classList.add('is-closing');
        const closeDelay = getFadeDuration() + getGrowDuration();
        closeTimer = window.setTimeout(() => {
          root.classList.remove('is-open');
          root.classList.remove('is-closing');
          collapseMinimalQtyControls();
          unlockPage();
          closeTimer = null;
        }, closeDelay);
        return;
      }

      root.classList.remove('is-open');
      collapseMinimalQtyControls();
      unlockPage();
    };

    const CART_ADD_SETTLE_MS = 520;
    const CART_MUTATION_SETTLE_MS = 150;
    const EASYGIFT_ADD_MARKER = '_easygift_internal';

    let queuedCartRefreshTimer = null;

    const isEasyGiftCartAddUrl = (url) => {
      try {
        return String(url || '').includes(EASYGIFT_ADD_MARKER);
      } catch (error) {
        return false;
      }
    };

    const scheduleCartRefresh = ({ openImmediately = false, delayMs = CART_MUTATION_SETTLE_MS, responseUrl = '' } = {}) => {
      const easyGiftAdd = isEasyGiftCartAddUrl(responseUrl);
      if (openImmediately && !easyGiftAdd) {
        openDrawer();
      }

      if (queuedCartRefreshTimer) {
        window.clearTimeout(queuedCartRefreshTimer);
      }

      queuedCartRefreshTimer = window.setTimeout(() => {
        queuedCartRefreshTimer = null;
        refreshDrawer();
      }, delayMs);
    };

    const openAndRefresh = () => {
      openDrawer();
      if (queuedCartRefreshTimer) {
        return refreshPromise || Promise.resolve();
      }
      return refreshDrawer();
    };

    window.KozlerCartDrawer = window.KozlerCartDrawer || {};
    window.KozlerCartDrawer.open = openDrawer;
    window.KozlerCartDrawer.close = closeDrawer;
    window.KozlerCartDrawer.refresh = refreshDrawer;
    window.KozlerCartDrawer.openAndRefresh = openAndRefresh;

    window.theme = window.theme || {};
    window.theme.cart = window.theme.cart || {};
    window.theme.cart.refresh = refreshDrawer;
    window.theme.cart.open = openAndRefresh;
    window.theme.cart.close = closeDrawer;

    const suppressRebuyDrawer = () => {
      const rebuyDrawers = document.querySelectorAll('#rebuy-cart, .rebuy-cart, [data-rebuy-cart]');
      rebuyDrawers.forEach((drawerEl) => {
        drawerEl.setAttribute('aria-hidden', 'true');
        drawerEl.classList.remove('is-visible');
        drawerEl.style.setProperty('display', 'none', 'important');
      });
      document.documentElement.classList.remove('rebuy-cart-open');
      document.body.classList.remove('rebuy-cart-open');
    };

    const ensureRebuySuppressionStyle = () => {
      if (document.getElementById('kozler-rebuy-suppress-style')) return;
      const styleEl = document.createElement('style');
      styleEl.id = 'kozler-rebuy-suppress-style';
      styleEl.textContent = '#rebuy-cart,.rebuy-cart,[data-rebuy-cart]{display:none!important;}';
      document.head.appendChild(styleEl);
    };

    const patchRebuyBridge = () => {
      window.Rebuy = window.Rebuy || {};
      window.Rebuy.Cart = window.Rebuy.Cart || {};
      window.Rebuy.SmartCart = window.Rebuy.SmartCart || {};
      window.Rebuy.Cart.show = () => {
        suppressRebuyDrawer();
        return openAndRefresh();
      };
      window.Rebuy.Cart.hide = closeDrawer;
      window.Rebuy.Cart.close = closeDrawer;
      window.Rebuy.SmartCart.show = () => {
        suppressRebuyDrawer();
        return openAndRefresh();
      };
      window.Rebuy.SmartCart.hide = closeDrawer;
      window.Rebuy.SmartCart.close = closeDrawer;
    };

    ensureRebuySuppressionStyle();
    patchRebuyBridge();
    suppressRebuyDrawer();
    let rebuyPatchAttempts = 0;
    const rebuyPatchTimer = window.setInterval(() => {
      rebuyPatchAttempts += 1;
      patchRebuyBridge();
      suppressRebuyDrawer();
      if (rebuyPatchAttempts >= 20) {
        window.clearInterval(rebuyPatchTimer);
      }
    }, 250);
    document.addEventListener('rebuy:smartcart.ready', () => {
      patchRebuyBridge();
      suppressRebuyDrawer();
    });

    const patchModalPortal = () => {
      const modalPortal = window.modalPortal;
      if (!modalPortal) return;

      if (typeof modalPortal.openModalById === 'function' && !modalPortal.__kozlerCartPatched) {
        const originalOpenModalById = modalPortal.openModalById.bind(modalPortal);
        modalPortal.openModalById = (id, ...args) => {
          if (id === 'CartDrawer') {
            openAndRefresh();
            return;
          }
          return originalOpenModalById(id, ...args);
        };
        modalPortal.__kozlerCartPatched = true;
      }

      // Avoid theme modalPortal playVideo/pauseVideo throwing when currentModalContent is null (race on close).
      if (typeof modalPortal.playVideo === 'function' && !modalPortal.__kozlerPlayVideoPatched) {
        const originalPlayVideo = modalPortal.playVideo.bind(modalPortal);
        modalPortal.playVideo = () => {
          if (!modalPortal.currentModalContent) return;
          return originalPlayVideo();
        };
        modalPortal.__kozlerPlayVideoPatched = true;
      }
      if (typeof modalPortal.pauseVideo === 'function' && !modalPortal.__kozlerPauseVideoPatched) {
        const originalPauseVideo = modalPortal.pauseVideo.bind(modalPortal);
        modalPortal.pauseVideo = () => {
          if (!modalPortal.currentModalContent) return;
          return originalPauseVideo();
        };
        modalPortal.__kozlerPauseVideoPatched = true;
      }
    };

    patchModalPortal();
    document.addEventListener('DOMContentLoaded', patchModalPortal, { once: true });
    setTimeout(patchModalPortal, 500);
    // Error monitoring: "ResizeObserver loop completed with undelivered notifications" is a benign
    // Chromium message—safe to ignore or filter in the SDK. "Script error." with no line/stack is
    // usually cross-origin third-party JS (analytics/apps); resolve via app updates or CORS, or ignore.
    // Any remaining querySelector/null reports should be re-triaged from fresh stack traces post-deploy.

    const getPathname = (urlString) => {
      try {
        return new URL(urlString, window.location.origin).pathname.replace(/\/+$/, '');
      } catch (error) {
        return '';
      }
    };

    const isCartAddPath = (pathname) => /\/cart\/add(?:\.js)?$/.test(pathname || '');
    const isCartMutationPath = (pathname) => /\/cart\/(?:change|update|clear)(?:\.js)?$/.test(pathname || '');

    if (!window.__kozlerCartFetchHookInstalled) {
      window.__kozlerCartFetchHookInstalled = true;
      const nativeFetch = window.fetch.bind(window);

      window.fetch = (...args) => {
        const responsePromise = nativeFetch(...args);
        responsePromise
          .then((response) => {
            if (!response || !response.ok || !response.url) return;
            const pathname = getPathname(response.url);
            if (isCartAddPath(pathname)) {
              scheduleCartRefresh({
                openImmediately: true,
                delayMs: CART_ADD_SETTLE_MS,
                responseUrl: response.url,
              });
              return;
            }
            if (isCartMutationPath(pathname)) {
              scheduleCartRefresh({ delayMs: CART_MUTATION_SETTLE_MS });
            }
          })
          .catch(() => {});
        return responsePromise;
      };
    }

    if (!window.__kozlerCartXhrHookInstalled && typeof window.XMLHttpRequest !== 'undefined') {
      window.__kozlerCartXhrHookInstalled = true;
      const nativeXhrOpen = window.XMLHttpRequest.prototype.open;
      const nativeXhrSend = window.XMLHttpRequest.prototype.send;

      window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
        this.__kozlerCartPathname = getPathname(url || '');
        return nativeXhrOpen.call(this, method, url, ...rest);
      };

      window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
        if (!this.__kozlerCartListenerAttached) {
          this.__kozlerCartListenerAttached = true;
          this.addEventListener('loadend', () => {
            const status = Number(this.status) || 0;
            if (status < 200 || status >= 300) return;
            const pathname = this.__kozlerCartPathname || getPathname(this.responseURL || '');
            if (isCartAddPath(pathname)) {
              scheduleCartRefresh({
                openImmediately: true,
                delayMs: CART_ADD_SETTLE_MS,
                responseUrl: this.responseURL || '',
              });
              return;
            }
            if (isCartMutationPath(pathname)) {
              scheduleCartRefresh({ delayMs: CART_MUTATION_SETTLE_MS });
            }
          });
        }
        return nativeXhrSend.apply(this, args);
      };
    }

    document.addEventListener('rebuy:cart.change', () => scheduleCartRefresh({ delayMs: CART_MUTATION_SETTLE_MS }));
    document.addEventListener('rebuy:cart:open', () => openAndRefresh());
    document.addEventListener('rebuy:cart:show', () => openAndRefresh());
    document.addEventListener('cart:updated', () => scheduleCartRefresh({ delayMs: CART_ADD_SETTLE_MS }));
    document.addEventListener('cart:change', () => scheduleCartRefresh({ delayMs: CART_MUTATION_SETTLE_MS }));
    document.addEventListener('cart:refresh', () => scheduleCartRefresh({ delayMs: CART_MUTATION_SETTLE_MS }));

    if (window.Shopify && window.Shopify.designMode) {
      const isThisSectionEvent = (event) => {
        const id = event?.detail?.sectionId;
        if (!id) return false;
        if (id === sectionId) return true;
        const host = root.closest('[id^="shopify-section-"]');
        return host && host.id === `shopify-section-${id}`;
      };

      document.addEventListener('shopify:section:select', (event) => {
        if (isThisSectionEvent(event)) openDrawer();
      });

      document.addEventListener('shopify:section:deselect', (event) => {
        if (isThisSectionEvent(event)) closeDrawer();
      });

      document.addEventListener('shopify:block:select', () => {
        if (!root.classList.contains('is-open')) openDrawer();
      });

      requestAnimationFrame(() => {
        const host = root.closest('[id^="shopify-section-"]');
        if (host && host.classList.contains('shopify-section--active')) {
          openDrawer();
        }
      });
    }

    document.addEventListener('click', (event) => {
      const openOnSubmitTrigger = closestFromEvent(event,`[${OPEN_ON_SUBMIT_ATTR}]`);
      if (openOnSubmitTrigger) {
        lastOpenOnSubmit = openOnSubmitTrigger;
        lastOpenOnSubmitAt = Date.now();
      } else {
        lastOpenOnSubmit = null;
        lastOpenOnSubmitAt = 0;
      }

      const opener = closestFromEvent(event,
        '[data-kozler-cart-open], [aria-controls="CartDrawer"], [data-cart-open], [data-cart-toggle], [data-cart-icon]'
      );
      const closer = closestFromEvent(event,'[data-kozler-cart-close], [data-cart-dismiss]');
      const overlay = closestFromEvent(event,'.kozler-cart__overlay');

      if (opener) {
        event.preventDefault();
        openAndRefresh();
        return;
      }

      if (closer || (overlay && root.dataset.overlayEnabled !== 'false')) {
        event.preventDefault();
        closeDrawer();
        return;
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const fallbackSubmitter = Date.now() - lastOpenOnSubmitAt < 2000 ? lastOpenOnSubmit : null;
      const submitter = event.submitter || fallbackSubmitter;
      if (!submitter || !submitter.hasAttribute(OPEN_ON_SUBMIT_ATTR)) return;
      if (submitter.form && submitter.form !== form) return;

      setTimeout(() => {
        if (event.defaultPrevented) return;
        scheduleCartRefresh({ openImmediately: true, delayMs: CART_ADD_SETTLE_MS });
      }, 0);

      lastOpenOnSubmit = null;
      lastOpenOnSubmitAt = 0;
    });

    document.addEventListener('click', (event) => {
      if (closestFromEvent(event,'[data-qty-minimal]')) return;
      collapseMinimalQtyControls();
    });

    document.addEventListener('click', (event) => {
      const openTooltip = root.querySelector('[data-terms-tooltip].is-open');
      if (!openTooltip) return;
      if (closestFromEvent(event,'[data-terms-tooltip]')) return;
      const trigger = openTooltip.querySelector('[data-terms-tooltip-trigger]');
      openTooltip.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    syncOneclickDuplicateVisibility();
    updateTermsState();
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((rootEl) => initKozlerCartDrawer(rootEl));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const loadedSectionId = event?.detail?.sectionId;
    if (!loadedSectionId) {
      initAll();
      return;
    }
    const rootEl = document.getElementById(`kozler-cart-${loadedSectionId}`);
    if (rootEl) initKozlerCartDrawer(rootEl);
  });
})();
